import dayjs from 'dayjs';

import {
  US_NEXUS_OPTIONS,
  TRANSACTION_VOLUME_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS
} from '#client/constants/additionalInfo';
import { DATE_FORMAT } from '#client/constants/date';

import AddressField from './components/AddressField';
import EditField from './components/EditField';
import Field from './components/Field';
import FullNameField from './components/FullNameField';
import IndustryField from './components/IndustryField';

import AccountPurposeUIField from '../CustomFields/AccountPurposeUIField';
import { AddressUIField } from '../CustomFields/AddressUIField';
import AnnualRevenueUIField from '../CustomFields/AnnualRevenueUIField';
import { BirthdayUIField } from '../CustomFields/BirthdayUIField';
import { BusinessAddressUIField } from '../CustomFields/BusinessAddressUIField';
import { BusinessWebsiteUIField } from '../CustomFields/BusinessWebsiteUIField';
import CardMailingAddressUIField from '../CustomFields/CardMailingAddressUIField';
import CashFlowUIField from '../CustomFields/CashFlowUIField';
import CountryUIField from '../CustomFields/CountryUIField';
import { EinUIField } from '../CustomFields/EinUIField';
import EmployUIField from '../CustomFields/EmployUIField';
import EntityTypeUIField from '../CustomFields/EntityTypeUIField';
import IndustryUIFieldVerticalList from '../CustomFields/IndustryUIFieldVerticalList';
import { Own75OrMoreUIField } from '../CustomFields/Own75OrMoreUIField';
import RegulatedStatusUIField from '../CustomFields/RegulatedStatusUIField';
import SourceOfFundsUIField from '../CustomFields/SourceOfFundsUIField';
import { SsnUIField } from '../CustomFields/SsnUIField';
import TransactionVolumeUIField from '../CustomFields/TransactionVolumeUIField';
import { UserAgreementsUIField } from '../CustomFields/UserAgreementsUIField';
import UsNexusUIField from '../CustomFields/UsNexusUIField';

export const ModelMetadata = {
  ein: new EditField('ein', 'Employer Identification Number (EIN)', {
    formatter: (value) => value || 'We’ll update your application when your EIN arrives',
    editQuestionData: {
      question:
        'You’ll need an EIN to finalize your bank account. Do you want to get an EIN from us?',
      comment: 'It’s a 9-digit employer identification number.',
      form: (field, { data, dataChangeHandler }) => (
        <EinUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  annualRevenue: new EditField('annualRevenue', 'Annual Revenue', {
    editQuestionData: {
      question: 'What is your expected annual business revenue?',
      form: (field, { data, dataChangeHandler }) => (
        <AnnualRevenueUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  naicsCode: new IndustryField('naicsCode', 'Industry', {
    editQuestionData: {
      question: 'What is your business’s industry type?',
      comment: 'Please select the option that best describes your primary business activity.',
      form: (field, { data, dataChangeHandler }) => (
        <IndustryUIFieldVerticalList
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  country: new EditField('countriesOfOperation', 'Country of Operation', {
    editQuestionData: {
      question: 'In which countries does your business currently operate?',
      comment:
        'Include all countries where you have customers, partners, employees, or conduct business activities.',
      form: (field, { data, dataChangeHandler }) => (
        <CountryUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  businessPurpose: new Field('businessPurpose', 'Business Purpose'),
  incorporationState: new Field('incorporationState', 'State of formation'),
  businessEntityName: new Field('businessEntityName', 'Business name'),
  businessEntityType: new Field('businessEntityType', 'Entity type'),
  phone: new Field('phone', 'Business phone'),
  businessAddress: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Address',
    {
      editQuestionData: {
        question: 'What is your physical business address?',
        comment:
          'This is the address we have on file. To meet requirements and avoid issues with your application, this must be a physical business address, not a virtual or Registered Agent address.',
        form: (field, { data, dataChangeHandler }) => (
          <BusinessAddressUIField
            allowDefaultSelection
            data={{
              addressLine1: data.businessAddressLine1,
              addressLine2: data.businessAddressLine2,
              city: data.businessAddressCity,
              state: data.businessAddressState,
              country: data.businessAddressCountry,
              zip: data.businessAddressZip
            }}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  businessAddressRA: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Address',
    {
      editQuestionData: {
        question: 'What is your physical business address?',
        comment:
          'This address will be associated with your bank account and will not override any of your formation application data.',
        form: (field, { data, dataChangeHandler }) => (
          <BusinessAddressUIField
            data={{
              addressLine1: data.businessAddressLine1,
              addressLine2: data.businessAddressLine2,
              city: data.businessAddressCity,
              state: data.businessAddressState,
              country: data.businessAddressCountry,
              zip: data.businessAddressZip
            }}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  operationalAddress: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Operational Address',
    {
      editQuestionData: {
        question: 'What is your physical business address?',
        comment:
          'This is the address we have on file. To meet requirements and avoid issues with your application, this must be a physical business address, not a virtual or Registered Agent address.',
        form: (field, { data, dataChangeHandler }) => (
          <BusinessAddressUIField
            allowDefaultSelection
            data={{
              addressLine1: data.operationalAddressLine1,
              addressLine2: data.operationalAddressLine2,
              city: data.operationalCity,
              state: data.operationalState,
              country: data.operationalCountry,
              zip: data.operationalPostalCode,
              valid: false
            }}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  operationalAddressRA: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Operational Address',
    {
      editQuestionData: {
        question: 'What is your physical business address?',
        comment:
          'This address will be associated with your bank account and will not override any of your formation application data.',
        form: (field, { data, dataChangeHandler }) => (
          <BusinessAddressUIField
            data={{
              addressLine1: data.operationalAddressLine1,
              addressLine2: data.operationalAddressLine2,
              city: data.operationalCity,
              state: data.operationalState,
              country: data.operationalCountry,
              zip: data.businessPostalCode,
              valid: false
            }}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  businessWebsiteUrl: new EditField('businessWebsiteUrl', 'Business website', {
    editQuestionData: {
      question: 'How do you market your business?',
      comment: 'We use this info to verify your business.',
      form: (field, { data, dataChangeHandler }) => (
        <BusinessWebsiteUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  cashFlow: new EditField('cashFlow', 'Cash Flow', {
    editQuestionData: {
      question: 'Describe your typical cash flow.',
      comment: 'This info helps us understand your business operations.',
      form: (field, { data, dataChangeHandler }) => (
        <CashFlowUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  agreements: new EditField('agreements', '', {
    editQuestionData: {
      question: 'Accept our user agreements',
      comment: 'Check the box if you’ve read and agree to the terms.',
      form: (field, { dataChangeHandler, data }) => (
        <UserAgreementsUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  numberOfEmployees: new EditField('numberOfEmployees', '', {
    editQuestionData: {
      question: 'How many employees do you currently employ?',
      form: (field, { data, dataChangeHandler }) => (
        <EmployUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  cardMailingAddress: new EditField('cardMailingAddress', '', {
    editQuestionData: {
      question: 'Where should we mail your debit card?',
      comment: 'For your security, your card must be mailed to an address on file.',
      form: (field, { data, dataChangeHandler }) => (
        <CardMailingAddressUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  entityType: new EditField('entityType', '', {
    editQuestionData: {
      question: 'What is your business’s legal entity type?',
      form: (field, { data, dataChangeHandler }) => (
        <EntityTypeUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  accountPurpose: new EditField('accountPurpose', '', {
    editQuestionData: {
      question: 'What is the primary purpose of this account?',
      comment: 'Please select the main reason your business is opening this account.',
      form: (field, { data, dataChangeHandler }) => (
        <AccountPurposeUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  sourceOfFunds: new EditField('sourceOfFunds', 'Primary source of funds', {
    formatter: (value) =>
      SOURCE_OF_FUNDS_OPTIONS.find((item) => item.value === value)?.label ?? value,
    editQuestionData: {
      question: 'What is the primary source of funds for your business?',
      comment: 'Select the option that best describes where your business funds come from.',
      form: (field, { data, dataChangeHandler }) => (
        <SourceOfFundsUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  transactionVolume: new EditField('transactionVolume', 'Expected monthly volume', {
    formatter: (value) =>
      TRANSACTION_VOLUME_OPTIONS.find((item) => item.value === value)?.label ?? value,
    editQuestionData: {
      question: 'What is your expected monthly transactions volume for this account?',
      form: (field, { data, dataChangeHandler }) => (
        <TransactionVolumeUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  regulatedStatus: new EditField('regulatorName', 'Regulated Status', {
    editQuestionData: {
      question: 'Is your business regulated by any government authority or financial regulator?',
      form: (field, { data, dataChangeHandler }) => (
        <RegulatedStatusUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  }),
  usNexus: new EditField('usNexus', 'U.S. operational Ties', {
    formatter: (value) =>
      value
        ?.split(',')
        .map((v) => US_NEXUS_OPTIONS.find((item) => item.value === v.trim())?.label ?? v.trim())
        .join(', '),
    editQuestionData: {
      question: 'Does your business have operational ties to the United States?',
      comment: 'e.g., employees, customers, physical locations, or banking relationships',
      form: (field, { data, dataChangeHandler }) => (
        <UsNexusUIField
          data={data}
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
        />
      )
    },
    require: true
  })
};

export const AccountMembersMetadata = {
  ssnLast: new Field('ssnLast', 'Social Security Number', {
    formatter: (value) => (value ? `••• - •• - ${value}` : '')
  }),
  ssn: new EditField('ssn', '', {
    editQuestionData: {
      question: 'What’s your Social Security number?',
      comment: 'We’re legally required to ask for your SSN.',
      form: (field, { data, dataChangeHandler }) => (
        <SsnUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={data}
        />
      )
    },
    require: true
  }),
  role: new Field('title', 'Title'),
  email: new Field('email', 'Email'),
  // TODO: Remove address field when remove LD flag: 'release-order-debit-card-next-up'
  address: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Address',
    {
      editQuestionData: {
        question: 'Confirm this is your personal address.',
        comment:
          'This address will be associated with your bank account and where we’ll send your debit card, so make sure it’s correct. It won’t override the contact info on your formation application.',
        form: (field, { data, dataChangeHandler }) => (
          <AddressUIField
            data={data}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  addressV2: new AddressField(
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'country',
    'zip',
    'Address',
    {
      editQuestionData: {
        question: 'Can you confirm this is your personal address?',
        comment:
          'This may be different from your business address, but will not override the contact info on your formation application.',
        form: (field, { data, dataChangeHandler }) => (
          <AddressUIField
            data={data}
            dataChangeHandler={dataChangeHandler}
            disabled={field.disabled}
            field={field}
          />
        )
      },
      require: true
    }
  ),
  fullName: new FullNameField('firstName', 'lastName', 'Primary Contact'),
  dateOfBirth: new EditField('dateOfBirth', 'Date of birth', {
    formatter: (value) => {
      const birthday = dayjs(value, DATE_FORMAT.BIRTHDAY);
      return birthday.isValid() ? birthday.format(DATE_FORMAT.BIRTHDAY) : '';
    },
    editQuestionData: {
      question: 'When’s your birthday?',
      form: (field, { data, dataChangeHandler }) => (
        <BirthdayUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  ownsSeventyFiveOrMore: new EditField('ownsSeventyFiveOrMore', '', {
    editQuestionData: {
      question: 'Do you own 75% or more of this business?',
      comment:
        'At this time, we cannot support businesses with owners that own less than 75% of the business.',
      form: (field, { data, dataChangeHandler }) => (
        <Own75OrMoreUIField
          dataChangeHandler={dataChangeHandler}
          disabled={field.disabled}
          field={field}
          value={field.getValue(data)}
        />
      )
    },
    require: true
  }),
  phone: new Field('phone', 'Contact Phone')
};
